(() => {
  window.WTCTheme?.register('[data-good-moods]', (root) => {
    const controller = new AbortController();
    const { signal } = controller;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
    const merchantMotion = root.dataset.motionEnabled !== 'false';
    const cups = [...root.querySelectorAll('.wtc-moods-cup')];
    const entries = [...root.querySelectorAll('[data-moods-detail]')].map((detail) => ({
      detail,
      summary: detail.querySelector('summary'),
      panel: detail.querySelector('.wtc-moods-panel'),
      target: detail.open,
      generation: 0,
      animation: null,
    }));
    let visible = false;
    let assetsReady = false;
    let entered = false;
    let entrance = [];
    let entranceGeneration = 0;
    let pointerFrame = 0;
    let pointerX = 0;
    let pointerY = 0;
    const motionEnabled = () => merchantMotion && !reduced.matches && !document.hidden;

    const resetPointer = () => {
      cancelAnimationFrame(pointerFrame);
      pointerFrame = 0;
      root.style.removeProperty('--moods-mx');
      root.style.removeProperty('--moods-my');
    };
    const stopEntrance = () => {
      entranceGeneration += 1;
      entrance.forEach((animation) => animation.cancel());
      entrance = [];
      delete root.dataset.entering;
    };
    const settle = (entry) => {
      entry.generation += 1;
      entry.animation?.cancel();
      entry.animation = null;
      entry.detail.open = entry.target;
      entry.summary.setAttribute('aria-expanded', String(entry.target));
      entry.panel.inert = !entry.target;
    };
    const expand = (entry, opening, animate = true) => {
      if (entry.target === opening && !entry.animation) return;
      const height = entry.detail.open ? entry.panel.getBoundingClientRect().height : 0;
      const opacity = entry.detail.open ? getComputedStyle(entry.panel).opacity : '0';
      const generation = ++entry.generation;
      entry.animation?.cancel();
      entry.animation = null;
      entry.target = opening;
      entry.summary.setAttribute('aria-expanded', String(opening));
      entry.panel.inert = !opening;
      if (!animate || !motionEnabled() || !entry.panel.animate) {
        entry.detail.open = opening;
        return;
      }
      // Keep the native details open until its closing panel has finished shrinking.
      entry.detail.open = true;
      const animation = entry.panel.animate([
        { height: `${height}px`, opacity },
        { height: `${opening ? entry.panel.scrollHeight : 0}px`, opacity: opening ? 1 : 0 },
      ], { duration: 380, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' });
      entry.animation = animation;
      const finish = () => {
        if (signal.aborted || generation !== entry.generation) return;
        entry.detail.open = entry.target;
        entry.animation = null;
        animation.cancel();
      };
      animation.finished.then(finish, finish);
    };
    const select = (entry, opening = true, animate = true) => {
      entered = true;
      stopEntrance();
      if (opening) root.dataset.story = entry.detail.dataset.pose;
      entries.forEach((item) => expand(item, item === entry && opening, animate));
    };

    entries.forEach((entry, index) => {
      entry.summary.setAttribute('aria-expanded', String(entry.target));
      entry.panel.inert = !entry.target;
      entry.summary.addEventListener('click', (event) => {
        event.preventDefault();
        select(entry, !entry.target);
      }, { signal });
      entry.summary.addEventListener('keydown', (event) => {
        let next;
        if (event.key === 'ArrowDown') next = (index + 1) % entries.length;
        if (event.key === 'ArrowUp') next = (index - 1 + entries.length) % entries.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = entries.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        entries[next].summary.focus();
      }, { signal });
    });

    const enter = () => {
      if (entered || !visible || !assetsReady || !motionEnabled() || signal.aborted) return;
      entered = true;
      if (!cups.every((cup) => cup.animate)) return;
      root.dataset.entering = '';
      const generation = ++entranceGeneration;
      entrance = cups.map((cup, index) => {
        const final = getComputedStyle(cup).transform;
        const direction = index ? 1 : -1;
        return cup.animate([
          { transform: `translateY(80px) rotate(${direction * 10}deg) scale(.87) ${final}`, opacity: 0 },
          { transform: `translateY(-10px) rotate(${-direction * 2}deg) ${final}`, opacity: 1, offset: .76 },
          { transform: final, opacity: 1 },
        ], { duration: 1100, delay: index * 130, easing: 'cubic-bezier(.22, 1, .36, 1)', fill: 'both' });
      });
      Promise.allSettled(entrance.map((animation) => animation.finished)).then(() => {
        if (!signal.aborted && generation === entranceGeneration) stopEntrance();
      });
    };

    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting && entry.intersectionRatio >= .12;
      if (visible) enter();
      else { stopEntrance(); resetPointer(); }
    }, { threshold: [0, .12] });
    observer.observe(root);
    const imagePromises = [...root.querySelectorAll('img')].map((image) => image.decode?.());
    Promise.allSettled([...imagePromises, document.fonts?.ready]).then(() => {
      if (signal.aborted) return;
      assetsReady = true;
      enter();
    });

    root.addEventListener('pointermove', (event) => {
      if (!visible || !motionEnabled() || !finePointer.matches || event.pointerType === 'touch') return;
      const bounds = root.getBoundingClientRect();
      pointerX = ((event.clientX - bounds.left) / bounds.width - .5) * 20;
      pointerY = ((event.clientY - bounds.top) / bounds.height - .5) * 13;
      if (pointerFrame) return;
      pointerFrame = requestAnimationFrame(() => {
        pointerFrame = 0;
        if (signal.aborted) return;
        root.style.setProperty('--moods-mx', `${pointerX.toFixed(2)}px`);
        root.style.setProperty('--moods-my', `${pointerY.toFixed(2)}px`);
      });
    }, { signal });
    root.addEventListener('pointerleave', resetPointer, { signal });
    root.addEventListener('shopify:block:select', (event) => {
      const entry = entries.find((item) => item.detail.contains(event.target) || item.detail.dataset.blockId === event.detail?.blockId);
      if (entry) select(entry, true, false);
    }, { signal });

    const syncMotion = () => {
      root.classList.toggle('is-motion-disabled', !merchantMotion || reduced.matches);
      stopEntrance();
      resetPointer();
      entries.forEach(settle);
      enter();
    };
    reduced.addEventListener('change', syncMotion, { signal });
    finePointer.addEventListener('change', resetPointer, { signal });
    document.addEventListener('visibilitychange', syncMotion, { signal });
    window.addEventListener('resize', () => {
      stopEntrance();
      resetPointer();
      entries.forEach(settle);
    }, { signal, passive: true });
    root.classList.toggle('is-motion-disabled', !merchantMotion || reduced.matches);

    return () => {
      controller.abort();
      observer.disconnect();
      stopEntrance();
      resetPointer();
      entries.forEach(settle);
    };
  });
})();
